/**
 * The **list layout's** vocabulary (docs/VIEWS.md §3): the column config a
 * board drawn on the list layout keeps in `payload.list` (board-view.ts), and a
 * view's list rail in its own `payload.list` (lens-view.ts). Rows x
 * configurable columns over the same set the board's other layouts draw.
 *
 * Column KEYS are opaque strings here, exactly like a board's `sort` key: the
 * column VOCABULARY (labels, minimum widths, cell renderers) is a view-layer
 * concern (`src/app/boards/list-columns.ts`) the domain must not import. The
 * shapes the domain does own are the ones the read layer needs: the
 * FIELD-column codec below (`fieldColumnKey`/`parseFieldColumnKey`), to know
 * which module values to resolve, and the built-in keys with their natural
 * directions (`LIST_COLUMN_KEYS`, `naturalListDir`), to order a listing
 * statement by a column as the list orders it (src/read/listing-criteria.ts).
 * An empty `columns` list means "the default column set" - decided by the view
 * layer, so the default can evolve without rewriting stored payloads.
 */

/** The list's built-in column keys, the title first: every key a list row
 *  sorts by beside the field columns (`sortValueOf`,
 *  src/app/boards/list-columns.ts, whose `BUILT_IN_COLUMNS` holds exactly the
 *  rest). The listing statement compiles a sort over each of them
 *  (`columnOrder`, src/read/listing-criteria.ts), so a new key fails the
 *  typecheck there until it compiles. */
export const LIST_COLUMN_KEYS = [
  'title',
  'state',
  'active',
  'closed',
  'delivered',
  'health',
  'assignee',
  'requester',
  'flow',
  'parent',
  'due',
  'priority',
  'updated',
  'created',
] as const;
export type ListColumnKey = (typeof LIST_COLUMN_KEYS)[number];

export function isListColumnKey(key: string): key is ListColumnKey {
  return (LIST_COLUMN_KEYS as readonly string[]).includes(key);
}

export type ListSortDir = 'asc' | 'desc';

/** A column's NATURAL direction - what one click gives (the Work sorts'
 *  `NATURAL_DIR` precedent): names and the due date read A to Z / soonest
 *  first, everything else newest / highest / furthest first. */
export function naturalListDir(key: string): ListSortDir {
  return key === 'title' || key === 'due' ? 'asc' : 'desc';
}

export interface ListColumn {
  /** Opaque column key: a built-in (`stage`, `assignee`, ...) or a field
   *  column minted by {@link fieldColumnKey}. */
  key: string;
  /** Persisted width in px, or null = the column's registry default. */
  width: number | null;
}

/** Width sanity clamp (px). The per-column MINIMUM lives in the view layer's
 *  registry; this is only the "a stored payload can never wedge the grid"
 *  defence, wide enough for any real column. */
export const LIST_COLUMN_WIDTH_MIN = 40;
export const LIST_COLUMN_WIDTH_MAX = 1600;

export function clampListColumnWidth(width: number): number {
  return Math.round(Math.min(LIST_COLUMN_WIDTH_MAX, Math.max(LIST_COLUMN_WIDTH_MIN, width)));
}

/**
 * Coerce stored or wire input into a valid column list: the one column
 * validator (a board's list layout and a view's list rail both run it).
 * Columns dedupe by key (first mention wins), keep only trimmed non-empty
 * keys, and clamp their widths. Anything malformed drops.
 */
export function normalizeListColumns(input: unknown): ListColumn[] {
  const columns: ListColumn[] = [];
  if (!Array.isArray(input)) return columns;
  const seen = new Set<string>();
  for (const raw of input) {
    if (!raw || typeof raw !== 'object') continue;
    const c = raw as Record<string, unknown>;
    const key = typeof c.key === 'string' ? c.key.trim() : '';
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const width =
      typeof c.width === 'number' && Number.isFinite(c.width)
        ? clampListColumnWidth(c.width)
        : null;
    columns.push({ key, width });
  }
  return columns;
}

// --- the field-column codec ---------------------------------------------------

/** Mint the column key of a custom field: `field:<moduleKey>:<fieldKey>`.
 *  `moduleKey` is a flow namespace (`flow:<definitionId>`) or a registry module
 *  key; both survive the round-trip because a module key contains at most the
 *  one namespace colon and never ends in one. */
export function fieldColumnKey(moduleKey: string, fieldKey: string): string {
  return `field:${moduleKey}:${fieldKey}`;
}

/** Decode a field-column key, or null when the key is not a field column.
 *  Total over junk (stored keys are untrusted): a malformed key reads as null,
 *  and the list simply renders nothing for it. */
export function parseFieldColumnKey(key: string): { moduleKey: string; fieldKey: string } | null {
  if (!key.startsWith('field:')) return null;
  const rest = key.slice('field:'.length);
  // A flow module key carries its own namespace colon: `flow:<definitionId>`.
  const offset = rest.startsWith('flow:') ? 'flow:'.length : 0;
  const split = rest.indexOf(':', offset);
  if (split <= 0 || split === rest.length - 1) return null;
  return { moduleKey: rest.slice(0, split), fieldKey: rest.slice(split + 1) };
}
